import { reactive, ref, type Ref } from 'vue';
import type { useWorkspaceStore } from '../stores/workspace';
import type { EntityDocument } from '../types/entities';

type DeleteReference = { id: string };
type ArchiveDialog = { entityId: string; title: string; references: Array<DeleteReference & { sourceEntityId: string; sourceTitle: string; sourceTemplateName: string; origin: string; fieldKey: string | null }> };
type PermanentDeleteDialog = { entityId: string; title: string; referenceCount: number };

export function useEntityManagementActions(options: {
  store: ReturnType<typeof useWorkspaceStore>;
  createOpen: Ref<boolean>;
  selectEntity: (id: string) => Promise<void>;
  activeEntity: Readonly<Ref<Pick<EntityDocument, 'id'> | null>>;
  isVersionReadonly: Readonly<Ref<boolean>>;
  draft: { fieldValues: Record<string, unknown> };
  scheduleSave: () => void;
  activeEntityCollectionIds: Ref<string[]>;
}) {
  const { store, createOpen, selectEntity, activeEntity, isVersionReadonly, draft, scheduleSave, activeEntityCollectionIds } = options;
  const deleteDialog = ref<ArchiveDialog | null>(null);
  const deleteReplacementIds = reactive<Record<string, string>>({});
  const deleteError = ref('');
  const permanentDeleteDialog = ref<PermanentDeleteDialog | null>(null);
  const permanentDeleteConfirmation = ref('');
  const permanentDeleteError = ref('');
async function openReferencedEntity(entityId: string) {
  createOpen.value = false;
  await selectEntity(entityId);
}

async function openArchiveDialog() {
  if (!activeEntity.value) return;
  deleteError.value = '';
  Object.keys(deleteReplacementIds).forEach((key) => delete deleteReplacementIds[key]);
  try {
    const impact = await store.getDeleteImpact(activeEntity.value.id);
    deleteDialog.value = { entityId: impact.entity.id, title: impact.entity.title, references: impact.references };
  } catch (cause) { deleteError.value = cause instanceof Error ? cause.message : 'Referenzen konnten nicht geprüft werden.'; }
}
async function confirmArchive() {
  if (!deleteDialog.value) return;
  deleteError.value = '';
  try {
    await store.archiveEntity(deleteDialog.value.entityId, deleteDialog.value.references.map((reference) => ({ referenceId: reference.id, replacementEntityId: deleteReplacementIds[reference.id] || null })));
    deleteDialog.value = null;
  } catch (cause) { deleteError.value = cause instanceof Error ? cause.message : 'Entity konnte nicht archiviert werden.'; }
}
async function openPermanentDeleteDialog() {
  if (!activeEntity.value) return;
  permanentDeleteError.value = '';
  permanentDeleteConfirmation.value = '';
  try {
    const impact = await store.getDeleteImpact(activeEntity.value.id);
    permanentDeleteDialog.value = { entityId: impact.entity.id, title: impact.entity.title, referenceCount: impact.references.length };
  } catch (cause) { permanentDeleteError.value = cause instanceof Error ? cause.message : 'Referenzen konnten nicht geprüft werden.'; }
}
async function confirmPermanentDelete() {
  if (!permanentDeleteDialog.value || permanentDeleteConfirmation.value !== 'LÖSCHEN') return;
  permanentDeleteError.value = '';
  try {
    await store.permanentlyDeleteEntity(permanentDeleteDialog.value.entityId);
    permanentDeleteDialog.value = null;
  } catch (cause) { permanentDeleteError.value = cause instanceof Error ? cause.message : 'Entity konnte nicht endgültig gelöscht werden.'; }
}

function setField(key: string, value: unknown) {
  if (isVersionReadonly.value) return;
  draft.fieldValues[key] = value;
  scheduleSave();
}
async function toggleActiveEntityCollection(collectionId: string, checked: boolean) {
  if (!activeEntity.value) return;
  try {
    if (checked) await store.addEntityToCategoryCollection(collectionId, activeEntity.value.id);
    else await store.removeEntityFromCategoryCollection(collectionId, activeEntity.value.id);
    activeEntityCollectionIds.value = checked ? [...new Set([...activeEntityCollectionIds.value, collectionId])] : activeEntityCollectionIds.value.filter((id) => id !== collectionId);
  } catch (error) { window.alert(error instanceof Error ? error.message : 'Sammlungszuordnung konnte nicht gespeichert werden.'); }
}
  return { deleteDialog, deleteReplacementIds, deleteError, permanentDeleteDialog, permanentDeleteConfirmation, permanentDeleteError,
    openReferencedEntity, openArchiveDialog, confirmArchive, openPermanentDeleteDialog, confirmPermanentDelete, setField, toggleActiveEntityCollection };
}
